import React from 'react';

import { Autocomplete, Box, Button, Checkbox, Chip, TextField } from '@mui/material';

export type AttributeSelectProps = {
  filterKey?: string;
  filters?: unknown;
  availability?: Record<string, boolean>;
  hasMore?: boolean;
  cursor?: string;
  serverSearch?: boolean;
  searchValue?: string;
  onSearch?: (value: string) => void;
  onNextPage?: () => void;
  loading?: boolean;
  searchError?: boolean;
  label: string;
  options: Array<string>;
  total?: number;
  selected: Array<string>;
  onChange: (values: Array<string>) => void;
};

export default function AttributeSelect({
  label,
  options,
  total,
  selected,
  onChange,
  availability,
  serverSearch,
  searchValue,
  onSearch,
  onNextPage,
  loading,
  searchError,
}: AttributeSelectProps) {
  const capped = total !== undefined && total > options.length;
  return (
    <div className="pm-attribute">
      <span>{label}</span>
      {onNextPage && (
        <Button size="small" onClick={onNextPage} disabled={loading}>
          Next choices
        </Button>
      )}
      <Autocomplete
        multiple
        freeSolo={serverSearch ? false : capped}
        loading={loading}
        inputValue={searchValue}
        onInputChange={(_, value, reason) => {
          if (reason === 'input' || reason === 'clear') onSearch?.(value);
        }}
        filterOptions={serverSearch ? (values) => values : undefined}
        disableCloseOnSelect
        size="small"
        limitTags={2}
        options={[...new Set([...options, ...selected])]}
        value={selected}
        onChange={(_, values) => onChange(values.map(String))}
        renderTags={(values, getTagProps) =>
          values.map((option, index) => {
            const { key, ...tagProps } = getTagProps({ index });
            return (
              <Chip
                key={key}
                {...tagProps}
                label={availability?.[option] === false ? `${option} · no data with current filters` : option}
                size="small"
                color={availability?.[option] === false ? 'warning' : 'default'}
                title={availability?.[option] === false ? `${option} · no data with current filters` : option}
              />
            );
          })
        }
        renderOption={(props, option, { selected: checked }) => (
          <Box component="li" {...props} key={option}>
            <Checkbox checked={checked} size="small" sx={{ mr: 0.5 }} />
            {option}
            {availability?.[option] === false ? ' · no data with current filters' : ''}
          </Box>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            error={searchError}
            helperText={searchError ? 'Choices could not be loaded. Change the search to retry.' : undefined}
            placeholder={selected.length ? undefined : capped ? 'All · type to search' : 'All'}
            inputProps={{ ...params.inputProps, 'aria-label': label }}
          />
        )}
        sx={{
          minWidth: 0,
          '& .MuiInputBase-root': { minHeight: 34, py: 0, fontSize: 12, borderRadius: '5px' },
          '& .MuiAutocomplete-tag': { maxWidth: 'calc(100% - 45px)', fontSize: 11 },
        }}
      />
    </div>
  );
}
